// client/src/components/ScheduleScreen.js
//
// Team tab — /teams/:teamId/schedule. Games and practices from the
// Schedule table, sorted by scheduled_at.
//
// Layout: hero card (next event) + upcoming list + recent list.
// Hero is "whatever's next" — game or practice, doesn't matter.
//
// v1 schema limitations worth remembering:
//   - No home_away field yet → skip "vs" / "@" prefix, just show opponent
//   - No scores yet → "Final" chip on past games shows no score
//   - No practice focus/title yet → practices show "Practice" + location only
// When those columns get added, this file needs updates in three places:
// game card body, recent-game chip, and practice card body. Search "SCHEMA-TODO".
//
// Time display: `scheduled_at` is the actual event time (the specific
// datetime the game/practice starts). `start_time`/`end_time` from the
// joined Availability row are the venue-block window (e.g. "we've got
// the rink 7am–2pm") — not what we show on cards. Different concepts.

import { useOutletContext } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { PageTitle } from './shell/PageTitle';
import { Scrawl } from './mischief/Scrawl';
import { Highlight } from './mischief/Highlight';
import { Sticker } from './mischief/Sticker';
import { MarkerCircle } from './mischief/MarkerCircle';

// ── Date/time formatting ────────────────────────────────────────────────
// Catalyst datetimes: "YYYY-MM-DD HH:MM:SS", no TZ. Same convention as
// Announcements' timeAgo. Normalizing with T + Z to make Date() happy.
function toDate(iso) {
  if (!iso) return null;
  // Catalyst returns "YYYY-MM-DD HH:MM:SS" without a timezone. Parse
  // as LOCAL time — youth sports schedules are entered as local wall-
  // clock times ("9am practice"), not UTC. Replacing space with T
  // gives Date() an ISO-ish string it parses in the browser's zone.
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T');
  return new Date(normalized);
}

function formatEventDate(iso) {
  const d = toDate(iso);
  if (!d) return '';
  // e.g. "SAT JUL 24"
  return d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  }).toUpperCase().replace(',', '');
}

function formatEventTime(iso) {
  const d = toDate(iso);
  if (!d) return '';
  // e.g. "9:00 AM"
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDayNumber(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return String(d.getDate());
}

function formatMonthAbbr(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
}

// ── partitionByTime ─────────────────────────────────────────────────────
// Split events into hero / upcoming / recent buckets. Endpoint returns
// scheduled_at DESC, so we reverse-and-partition to get upcoming ASC
// (soonest first) and recent DESC (most recent first).
function partitionByTime(events) {
  const now = Date.now();
  const upcoming = [];
  const recent = [];

  for (const e of events) {
    const t = toDate(e.scheduled_at)?.getTime();
    if (t == null) continue;                // skip malformed rows
    if (t >= now) upcoming.push(e);
    else recent.push(e);
  }

  // Upcoming: soonest first (ascending). Endpoint gave us DESC, so reverse.
  upcoming.reverse();

  // Recent: most recent first (descending). Endpoint order already correct.

  // Hero is the first upcoming event.
  const [hero, ...restUpcoming] = upcoming;

  return { hero: hero ?? null, upcoming: restUpcoming, recent };
}

// ── HeroCard ────────────────────────────────────────────────────────────
// Big anchor card for the next event. Games get marker-circle around
// opponent; practices get a plainer treatment since there's no opponent.
function HeroCard({ event }) {
  const isGame = event.type === 'Game';
  const isTournament = event.status === 'Tournament';
  const cancelled = event.status === 'Cancelled' || event.status === 'Postponed';

  return (
    <article
      className={`
        relative rounded-2xl bg-paper border border-rule
        px-5 py-5
        ${cancelled ? 'opacity-60' : ''}
      `}
    >
      {/* Small eyebrow with date + time */}
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        {formatEventDate(event.scheduled_at)} · {formatEventTime(event.scheduled_at)}
      </div>

      {/* Primary line: opponent for games, "Practice" for practices */}
      <h2 className="font-serif text-[26px] leading-tight text-ink mt-1">
        {isGame ? (
          isTournament ? (
            <MarkerCircle color="marker" padding="tight">
              {event.opponent || 'TBD'}
            </MarkerCircle>
          ) : (
            <MarkerCircle color="marker" padding="tight">
              {event.opponent || 'TBD'}
            </MarkerCircle>
          )
        ) : (
          'Practice'
        )}
      </h2>

      {/* Venue line */}
      <div className="text-[12px] text-inkSub mt-2">
        {event.location_name}
        {event.location_city && (
          <span> · {event.location_city}{event.location_state && `, ${event.location_state}`}</span>
        )}
      </div>

      {/* Status sticker — top-right, tilted */}
      {isTournament && (
        <Sticker tone="marker" className="absolute -top-2 right-4">
          CUP
        </Sticker>
      )}
      {cancelled && (
        <Sticker tone="default" className="absolute -top-2 right-4">
          {event.status?.toUpperCase()}
        </Sticker>
      )}
    </article>
  );
}

// ── ListRow ─────────────────────────────────────────────────────────────
// Editorial list row from the design brief: 54px day-number | 1fr body | auto chip
function ListRow({ event, showResult = false }) {
  const isGame = event.type === 'Game';
  const isTournament = event.status === 'Tournament';
  const cancelled = event.status === 'Cancelled' || event.status === 'Postponed';

  const primaryText = isGame ? (event.opponent || 'TBD') : 'Practice';
  const venueText = [
    formatEventTime(event.scheduled_at),
    event.location_name,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className={`
        py-3.5 border-t border-rule
        grid grid-cols-[54px_1fr_auto] gap-3.5 items-center
        ${cancelled ? 'opacity-60' : ''}
      `}
    >
      {/* Day number + month */}
      <div>
        <div className="inline-block">
          {isTournament ? (
            <Highlight color="yellow" rotate={-2}>
              <span className="font-serif text-[26px] leading-none px-[3px]">
                {formatDayNumber(event.scheduled_at)}
              </span>
            </Highlight>
          ) : (
            <span className="font-serif text-[26px] leading-none">
              {formatDayNumber(event.scheduled_at)}
            </span>
          )}
        </div>
        <div className="text-[9px] tracking-[0.1em] font-bold uppercase text-inkSub mt-0.5">
          {formatMonthAbbr(event.scheduled_at)}
        </div>
      </div>

      {/* Body: primary + venue line */}
      <div>
        <div className="text-[14px] font-semibold text-ink">
          {primaryText}
        </div>
        <div className="text-[11px] text-inkSub mt-0.5">
          {venueText}
        </div>
      </div>

      {/* Right: sticker for tournament / cancelled / (SCHEMA-TODO: W/L for finals) */}
      <div>
        {isTournament && <Sticker tone="marker">CUP</Sticker>}
        {cancelled && <Sticker tone="default">{event.status?.toUpperCase()}</Sticker>}
        {showResult && event.status === 'Final' && (
          // SCHEMA-TODO: no scores in schema yet. When home_score/away_score
          // land, wrap this in a "W X–Y" / "L X–Y" derivation.
          <Sticker tone="default">FINAL</Sticker>
        )}
      </div>
    </div>
  );
}

// ── Section header ──────────────────────────────────────────────────────
function SectionLabel({ children, count }) {
  return (
    <div className="mt-8 mb-2 flex items-baseline justify-between">
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        {children}
      </div>
      {count != null && (
        <div className="text-[11px] text-inkSub">
          {count} {count === 1 ? 'event' : 'events'}
        </div>
      )}
    </div>
  );
}

// ── ScheduleScreen ──────────────────────────────────────────────────────
export function ScheduleScreen() {
  const { team } = useOutletContext();
  const { data, loading, error } = useFetch(
  team?.id ? `/teams/${team.id}/schedule` : null);

  if (loading) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Schedule
        </PageTitle>
        <div className="mt-8">
          <div className="rounded-2xl bg-paper border border-rule px-5 py-5 animate-pulse">
            <div className="h-3 bg-rule rounded w-1/3" />
            <div className="mt-3 h-8 bg-rule rounded w-2/3" />
            <div className="mt-3 h-3 bg-rule rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Schedule
        </PageTitle>
        <div className="mt-6 rounded-2xl bg-paper border border-rule px-5 py-4">
          <h3 className="font-serif text-[20px] text-ink">Something's off.</h3>
          <Scrawl className="mt-2" color="inkSub">
            couldn't load the schedule — try refresh?
          </Scrawl>
        </div>
      </div>
    );
  }

  const { hero, upcoming, recent } = partitionByTime(data ?? []);
  const hasAnything = hero || upcoming.length > 0 || recent.length > 0;

  return (
    <div className="px-5 pt-6 pb-8">
      <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
        Schedule
      </PageTitle>

      {!hasAnything && (
        <div className="text-center pt-10 pb-4">
          <div className="font-serif text-[32px] text-ink leading-tight">
            Nothin' on the books yet
          </div>
          <Scrawl className="mt-2 inline-block" color="inkSub">
            check back later 🏒
          </Scrawl>
        </div>
      )}

      {hero && (
        <div className="mt-6">
          <HeroCard event={hero} />
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <SectionLabel count={upcoming.length}>Upcoming</SectionLabel>
          <div>
            {upcoming.map(e => <ListRow key={e.id} event={e} />)}
          </div>
        </>
      )}

      {recent.length > 0 && (
        <>
          <SectionLabel count={recent.length}>Recent</SectionLabel>
          <div>
            {recent.map(e => <ListRow key={e.id} event={e} showResult />)}
          </div>
        </>
      )}
    </div>
  );
}